import { Container, Text, type TextStyleOptions } from 'pixi.js';

export interface HUDState {
  score: number;
  lives: number;
}

export interface HUDConfig {
  canvasWidth: number;
  padding?: number;
}

export interface HUD {
  container: Container;
  scoreText: Text;
  livesText: Text;
  update(state: HUDState): void;
  destroy(): void;
}

const HUD_TEXT_STYLE: TextStyleOptions = {
  fontFamily: 'monospace',
  fontSize: 20,
  fontWeight: 'bold',
  fill: 0xffffff,
};

const DEFAULT_PADDING = 16;

function formatScore(score: number): string {
  return `SCORE: ${String(score).padStart(4, '0')}`;
}

function formatLives(lives: number): string {
  return `LIVES: ${lives}`;
}

export function createHUD(parent: Container, config: HUDConfig): HUD {
  const padding = config.padding ?? DEFAULT_PADDING;

  const container = new Container();
  container.label = 'hud';
  parent.addChild(container);

  const scoreText = new Text({ text: formatScore(0), style: HUD_TEXT_STYLE });
  scoreText.label = 'hud-score';
  scoreText.x = padding;
  scoreText.y = padding;
  container.addChild(scoreText);

  const livesText = new Text({ text: formatLives(0), style: HUD_TEXT_STYLE });
  livesText.label = 'hud-lives';
  livesText.anchor.set(1, 0);
  livesText.x = config.canvasWidth - padding;
  livesText.y = padding;
  container.addChild(livesText);

  function update(state: HUDState): void {
    scoreText.text = formatScore(state.score);
    livesText.text = formatLives(state.lives);
  }

  function destroy(): void {
    container.destroy({ children: true });
  }

  return { container, scoreText, livesText, update, destroy };
}
